import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, CheckCircle, CreditCard, Clock, ExternalLink } from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const API_BASE = '/api/client-portal';

const PortalStaging = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('client_portal_token');
      const res = await fetch(`${API_BASE}/staging/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load staging status');
      const result = await res.json();

      if (!result.has_staging) {
        clientPortalService.clearStagingFlag();
        navigate('/portal/dashboard', { replace: true });
        return;
      }
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Poll every 30s for payment completion
  useEffect(() => {
    if (!data || (data.signature_completed && data.payment_completed)) return;
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [data, fetchStatus]);

  // If both complete, clear flag and redirect
  useEffect(() => {
    if (data && data.signature_completed && data.payment_completed) {
      clientPortalService.clearStagingFlag();
      setTimeout(() => navigate('/portal/dashboard', { replace: true }), 3000);
    }
  }, [data, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center px-4">
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-6 text-red-300 max-w-md">
          <p>{error}</p>
          <button onClick={fetchStatus} className="mt-4 px-4 py-2 bg-red-500/30 rounded hover:bg-red-500/40">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const allComplete = data.signature_completed && data.payment_completed;

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center px-4">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white">Complete Your Enrollment</h1>
          <p className="text-virgo-teal-400 mt-2">
            {data.practice_area && `${data.practice_area} \u2014 `}Please complete the steps below to activate your case.
          </p>
        </div>

        <div className="space-y-4">
          {/* Step 1: Signature */}
          <div className={`bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border p-6 ${data.signature_completed ? 'border-green-500/50' : 'border-cyan-500/50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {data.signature_completed ? (
                  <CheckCircle className="w-6 h-6 text-green-400" />
                ) : (
                  <FileText className="w-6 h-6 text-cyan-400" />
                )}
                <div>
                  <h3 className="text-white font-semibold">Sign Engagement Letter</h3>
                  <p className="text-virgo-teal-400 text-sm">
                    {data.signature_completed ? 'Signed' : (data.document_title || 'Review and sign your engagement letter')}
                  </p>
                </div>
              </div>
              {!data.signature_completed && data.signing_token && (
                <a
                  href={`/staging/${data.signing_token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-1 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors text-sm font-medium"
                >
                  <span>Sign</span>
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>

          {/* Step 2: Payment */}
          <div className={`bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border p-6 ${data.payment_completed ? 'border-green-500/50' : 'border-virgo-teal-700/30'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {data.payment_completed ? (
                  <CheckCircle className="w-6 h-6 text-green-400" />
                ) : (
                  <CreditCard className="w-6 h-6 text-virgo-teal-400" />
                )}
                <div>
                  <h3 className="text-white font-semibold">Pay Retainer</h3>
                  <p className="text-virgo-teal-400 text-sm">
                    {data.payment_completed
                      ? 'Payment received'
                      : data.retainer_amount
                        ? `$${data.retainer_amount.toLocaleString()} \u2014 Invoice ${data.invoice_number || ''}`
                        : 'Complete your retainer payment'}
                  </p>
                </div>
              </div>
              {!data.payment_completed && data.square_payment_url && (
                <a
                  href={data.square_payment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-1 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors text-sm font-medium"
                >
                  <span>Pay</span>
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>

          {/* Step 3: Complete */}
          {allComplete && (
            <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 rounded-xl border border-green-500/50 p-6 text-center">
              <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
              <h3 className="text-white font-semibold text-lg">Your Portal is Ready</h3>
              <p className="text-green-300 text-sm mt-1">Redirecting to your dashboard...</p>
            </div>
          )}

          {/* Pending indicator */}
          {!allComplete && (
            <div className="flex items-center justify-center space-x-2 text-virgo-teal-500 text-sm mt-4">
              <Clock size={14} />
              <span>This page updates automatically</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalStaging;
